import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsApi, Notification } from '../api/notifications';
import PremiumLayout from '../components/PremiumLayout';
import { 
  BellIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowRightIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const GENERAL_LABEL = 'General';

const Notifications: React.FC = () => {
  const { user } = useAuth();
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: notifications = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['notifications', user?.id, showUnreadOnly],
    queryFn: () => notificationsApi.getAll(showUnreadOnly),
    refetchInterval: 30000,
    retry: 1,
    enabled: !!user?.id,
  });

  // Unique projects from notifications (for filter dropdown)
  const projectList = useMemo(() => {
    const set = new Set<string>();
    notifications.forEach((n) => set.add(n.project && n.project.trim() ? n.project.trim() : GENERAL_LABEL));
    return [GENERAL_LABEL, ...Array.from(set).filter((p) => p !== GENERAL_LABEL).sort()];
  }, [notifications]);

  // Filter by selected project, then group by project
  const filteredByProject = useMemo(
    () =>
      !selectedProject
        ? notifications
        : notifications.filter((n) => (n.project && n.project.trim() ? n.project.trim() : GENERAL_LABEL) === selectedProject),
    [notifications, selectedProject]
  );

  const groupedByProject = useMemo(() => {
    const map = new Map<string, Notification[]>();
    filteredByProject.forEach((n) => {
      const key = n.project && n.project.trim() ? n.project.trim() : GENERAL_LABEL;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(n);
    });
    // Sort groups: General last, rest alphabetically
    const general = map.get(GENERAL_LABEL);
    map.delete(GENERAL_LABEL);
    const sorted = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    if (general?.length) sorted.push([GENERAL_LABEL, general]);
    return sorted;
  }, [filteredByProject]);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count', user?.id],
    queryFn: notificationsApi.getUnreadCount,
    enabled: !!user?.id,
  });

  const markAsReadMutation = useMutation({
    mutationFn: notificationsApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: notificationsApi.markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: notificationsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: notificationsApi.clearAll,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      setSelectedProject('');
    },
  });

  const handleMarkAsRead = (id: number) => {
    markAsReadMutation.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const handleClearAll = () => {
    if (window.confirm('Clear all notifications? This cannot be undone.')) {
      clearAllMutation.mutate();
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'overdue':
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'success':
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const handleNotificationClick = (notification: any) => {
    // Mark as read when clicked
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }

    // Navigate based on entity type
    if (notification.entity_type === 'task' && notification.entity_id) {
      // Navigate to tasks page with the task ID in state or URL
      navigate('/tasks', { 
        state: { 
          highlightTaskId: notification.entity_id,
          scrollToTask: true,
          notificationId: notification.id
        } 
      });
    } else if (notification.entity_type === 'lead' && notification.entity_id) {
      // Navigate to lead detail page
      navigate(`/leads/${notification.entity_id}`);
    } else if (notification.entity_type === 'account' && notification.entity_id) {
      // Navigate to account detail page
      navigate(`/accounts/${notification.entity_id}`);
    }
  };

  const isClickable = (notification: any) => {
    return notification.entity_type && notification.entity_id && 
           (notification.entity_type === 'task' || 
            notification.entity_type === 'lead' || 
            notification.entity_type === 'account');
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Notifications">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  if (isError) {
    const ax = error as { response?: { data?: { error?: string } }; message?: string };
    const errMessage = ax?.response?.data?.error || ax?.message || 'Failed to load notifications.';
    return (
      <PremiumLayout title="Notifications">
        <div className="premium-card p-8 text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <p className="text-gray-700 font-medium mb-2">Something went wrong</p>
          <p className="text-sm text-gray-500 mb-6">{errMessage}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="btn-primary"
          >
            Try again
          </button>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Notifications"
      subtitle={`${unreadCount} unread notifications`}
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="premium-card p-4">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showUnreadOnly}
                  onChange={(e) => setShowUnreadOnly(e.target.checked)}
                  className="mr-2"
                />
                Show unread only
              </label>
              <div className="flex items-center gap-2">
                <FolderIcon className="h-5 w-5 text-gray-500" />
                <label htmlFor="project-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Project:
                </label>
                <select
                  id="project-filter"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">All projects</option>
                  {projectList.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                >
                  Mark all as read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="inline-flex items-center text-red-600 hover:text-red-900 text-sm font-medium"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Notifications List - grouped by project */}
        <div className="premium-card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <BellIcon className="h-6 w-6 mr-2" />
            Notifications ({filteredByProject.length})
            {selectedProject && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                in {selectedProject}
              </span>
            )}
          </h2>
          {filteredByProject.length === 0 ? (
            <div className="text-center py-12">
              <BellIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {notifications.length === 0
                  ? 'No notifications found'
                  : `No notifications for ${selectedProject || 'this filter'}`}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {groupedByProject.map(([projectName, list]) => (
                <div key={projectName}>
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
                    <FolderIcon className="h-4 w-4 text-indigo-500" />
                    {projectName}
                    <span className="text-gray-500 font-normal">({list.length})</span>
                  </h3>
                  <div className="space-y-3">
                    {list.map((notification) => {
                const clickable = isClickable(notification);
                return (
                  <div
                    key={notification.id}
                    className={`border rounded-lg p-4 ${
                      notification.is_read
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-white border-indigo-200 shadow-sm'
                    } ${
                      clickable ? 'cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all' : ''
                    }`}
                    onClick={() => clickable && handleNotificationClick(notification)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start flex-1">
                        {notification.entity_type === 'task' ? (
                          <ClipboardDocumentListIcon className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                        ) : (
                          getCategoryIcon(notification.category || 'info')
                        )}
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className={`text-sm font-semibold ${
                              notification.is_read ? 'text-gray-600' : 'text-gray-900'
                            }`}>
                              {notification.title}
                            </h3>
                            <div className="flex items-center space-x-2">
                              {clickable && (
                                <span className="text-xs text-indigo-600 flex items-center">
                                  Click to view
                                  <ArrowRightIcon className="h-3 w-3 ml-1" />
                                </span>
                              )}
                              {!notification.is_read && (
                                <span className="ml-2 w-2 h-2 bg-indigo-600 rounded-full"></span>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div 
                        className="flex items-center space-x-2 ml-4"
                        onClick={(e) => e.stopPropagation()} // Prevent navigation when clicking buttons
                      >
                        {!notification.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="text-indigo-600 hover:text-indigo-900 text-sm"
                          >
                            Mark as read
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(notification.id)}
                          className="inline-flex items-center text-red-600 hover:text-red-900 text-sm font-medium"
                          title="Clear notification"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PremiumLayout>
  );
};

export default Notifications;
